//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled, { useTheme } from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import { Icon } from '@components/atoms/icon/Icon';
import { BodyText, SubsectionText } from '@components/atoms/text/Text';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';

interface Props {
  type: ToastType;
  title: string;
  message: string;
  onClose?: () => void;
}

function ToastIcon({ type }: { type: ToastType }) {
  const theme = useTheme();
  switch (type) {
    case ToastType.INFO:
      return <StyledIcon name='info' color={theme.colors.aliases.primary} />;
    case ToastType.SUCCESS:
      return (
        <StyledIcon
          name='circle-checkmark'
          color={theme.colors.aliases.success}
        />
      );
    case ToastType.ERROR:
      return <StyledIcon name='error' color={theme.colors.aliases.error} />;
    // TODO: Proper warning color
    case ToastType.WARNING:
      return <StyledIcon name='alarm' color={theme.colors.aliases.error} />;
  }
}

export const Toast = ({ type: toastType, title, message, onClose }: Props) => {
  return (
    <Toasty className='cu-bs-3 cu-mt-2 cu-mr-2 cu-p-4'>
      <Header>
        <Row>
          <ToastIcon type={toastType} />
          <SubsectionText offset={0}>{title}</SubsectionText>
        </Row>
        <InvisibleButton type={'button'} iconName={'close'} onClick={onClose} />
      </Header>
      <BodyText>{message}</BodyText>
    </Toasty>
  );
};

const Row = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.spacings[1]};
`,
);

const Header = styled.div(
  ({ theme }) => `
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacings[2]};
`,
);

const Toasty = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
  max-width: 332px;
  z-index: ${({ theme }) => theme.zindex.fixed};
`;

const StyledIcon = styled(Icon)<{ color: string }>(
  ({ color }) => `
  color: ${color};
`,
);
